import React, { useState } from 'react';
import {
  Badge,
  Box,
  Divider,
  Flex,
  HStack,
  Icon,
  Skeleton,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import Button from 'components/ui/Button';
import { useBillingAccount, useReceipts } from 'hooks/useBillingAccount';
import { BillingCharge, ChargeSourceType, Patient, PaymentMethodType } from 'types/models';
import {
  MdCreditCard,
  MdCheckCircle,
  MdAccountBalanceWallet,
  MdSwapHoriz,
  MdCalendarToday,
  MdAssignment,
  MdMonitor,
  MdEdit,
  MdLocalOffer,
} from 'react-icons/md';
import RecordPaymentModal from './RecordPaymentModal';

// ─── Constants ────────────────────────────────────────────────────────────────

function fmtDate(iso: string): string {
  const d = new Date(iso);
  const days   = ['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'];
  const months = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];
  const pad = (n: number) => String(n).padStart(2,'0');
  return `${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()} · ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fmtMoney(n: number): string {
  return `$${n.toFixed(2)}`;
}


// ─── Charge source ────────────────────────────────────────────────────────────

const SOURCE_ICON: Record<ChargeSourceType, React.ElementType> = {
  [ChargeSourceType.SESSION]:          MdCalendarToday,
  [ChargeSourceType.PLAN_ACTIVATION]:  MdAssignment,
  [ChargeSourceType.EVALUATION]:       MdMonitor,
  [ChargeSourceType.MANUAL]:           MdEdit,
};

const SOURCE_LABEL: Record<ChargeSourceType, string> = {
  [ChargeSourceType.SESSION]:          'Sesión',
  [ChargeSourceType.PLAN_ACTIVATION]:  'Activación plan',
  [ChargeSourceType.EVALUATION]:       'Evaluación',
  [ChargeSourceType.MANUAL]:           'Manual',
};

const SOURCE_COLOR: Record<ChargeSourceType, string> = {
  [ChargeSourceType.SESSION]:          'blue',
  [ChargeSourceType.PLAN_ACTIVATION]:  'purple',
  [ChargeSourceType.EVALUATION]:       'cyan',
  [ChargeSourceType.MANUAL]:           'gray',
};

// ─── Charge row ───────────────────────────────────────────────────────────────

function ChargeRow({ charge }: { charge: BillingCharge }) {
  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.100');
  const bgHover     = useColorModeValue('gray.50',  'navy.700');
  const textColor   = useColorModeValue('gray.800', 'white');
  const mutedColor  = useColorModeValue('gray.500', 'gray.400');

  const srcType    = charge.sourceType as ChargeSourceType;
  const SrcIcon    = SOURCE_ICON[srcType]  ?? MdEdit;
  const srcLabel   = SOURCE_LABEL[srcType] ?? srcType;
  const srcColor   = SOURCE_COLOR[srcType] ?? 'gray';

  const basePrice     = charge.basePrice     != null ? Number(charge.basePrice)     : null;
  const discountAmt   = charge.discountAmount != null ? Number(charge.discountAmount) : null;
  const hasDiscount   = discountAmt != null && discountAmt > 0;
  const total         = Number(charge.amount);

  // Extract service name from description (before " | Descuento:")
  const [servicePart, ...rest] = charge.description.split(' | Descuento:');
  const discountDesc = rest.join(' | Descuento:').trim();

  return (
    <Flex
      align="flex-start"
      gap="14px"
      px="20px" py="14px"
      borderBottom="1px solid" borderColor={borderColor}
      _hover={{ bg: bgHover }}
      transition="background 0.15s"
      _last={{ borderBottom: 'none' }}
    >
      {/* Source icon */}
      <Flex
        w="38px" h="38px" borderRadius="10px" flexShrink={0}
        align="center" justify="center"
        bg={`${srcColor}.50`}
      >
        <Icon as={SrcIcon} w="18px" h="18px" color={`${srcColor}.500`} />
      </Flex>

      {/* Info */}
      <Box flex="1" minW={0}>
        <HStack spacing="8px" mb="3px" flexWrap="wrap">
          <Text fontSize="sm" fontWeight="700" color={textColor} noOfLines={1}>
            {servicePart}
          </Text>
          <Badge colorScheme={srcColor} variant="subtle" borderRadius="full" px="8px" fontSize="10px">
            {srcLabel}
          </Badge>
        </HStack>

        {/* Price breakdown */}
        <HStack spacing="12px" flexWrap="wrap">
          {basePrice != null && hasDiscount && (
            <Text fontSize="xs" color={mutedColor}>
              Base: <Text as="span" fontWeight="600">{fmtMoney(basePrice)}</Text>
            </Text>
          )}
          {hasDiscount && (
            <HStack spacing="4px">
              <Icon as={MdLocalOffer} color="red.400" w="11px" h="11px" />
              <Text fontSize="xs" color="red.500" fontWeight="600">
                -{fmtMoney(discountAmt!)}
              </Text>
            </HStack>
          )}
          {hasDiscount && discountDesc && (
            <Text fontSize="xs" color={mutedColor} noOfLines={1} maxW="200px" fontStyle="italic">
              {discountDesc}
            </Text>
          )}
        </HStack>

        <Text fontSize="xs" color={mutedColor} mt="2px">{fmtDate(charge.createdAt)}</Text>
      </Box>

      {/* Total */}
      <Box textAlign="right" flexShrink={0}>
        <Text fontSize="sm" fontWeight="800" color={textColor}>{fmtMoney(total)}</Text>
        {hasDiscount && basePrice != null && (
          <Text fontSize="10px" color={mutedColor} textDecoration="line-through">
            {fmtMoney(basePrice)}
          </Text>
        )}
      </Box>
    </Flex>
  );
}

const METHOD_ICON: Record<PaymentMethodType, React.ElementType> = {
  [PaymentMethodType.EFECTIVO]:      MdAccountBalanceWallet,
  [PaymentMethodType.TARJETA]:       MdCreditCard,
  [PaymentMethodType.TRANSFERENCIA]: MdSwapHoriz,
};

const METHOD_LABEL: Record<PaymentMethodType, string> = {
  [PaymentMethodType.EFECTIVO]:      'Efectivo',
  [PaymentMethodType.TARJETA]:       'Tarjeta',
  [PaymentMethodType.TRANSFERENCIA]: 'Transferencia',
};

// ─── Billing account summary ──────────────────────────────────────────────────

interface SummaryProps {
  patientId:   string;
  patientName: string;
}

function BillingAccountSummary({ patientId, patientName }: SummaryProps) {
  const { data: account, isLoading }    = useBillingAccount(patientId);
  const { data: receipts = [], isLoading: loadingReceipts } = useReceipts(patientId);
  const [payOpen, setPayOpen] = useState(false);

  const cardBg      = useColorModeValue('white',     'navy.800');
  const borderColor = useColorModeValue('gray.100',  'whiteAlpha.100');
  const textColor   = useColorModeValue('gray.800',  'white');
  const mutedColor  = useColorModeValue('gray.500',  'gray.400');

  const balance      = account?.balance ?? { totalCharged: 0, totalPaid: 0, currentDebt: 0 };
  const totalCharged = Number(balance.totalCharged);
  const totalPaid    = Number(balance.totalPaid);
  const currentDebt  = Number(balance.currentDebt);
  const hasDebt      = currentDebt > 0;
  const alDia        = !hasDebt && totalCharged > 0;

  if (isLoading) {
    return <Skeleton h="100px" borderRadius="14px" mb="20px" />;
  }

  return (
    <>
      <Box
        bg={cardBg}
        border="1px solid"
        borderColor={hasDebt ? 'orange.200' : alDia ? 'green.200' : borderColor}
        borderRadius="14px"
        px="20px" py="16px"
        mb="20px"
      >
        {/* Header row */}
        <HStack justify="space-between" mb="12px" flexWrap="wrap" gap="8px">
          <HStack spacing="8px">
            <Text fontSize="xs" fontWeight="700" color={mutedColor} textTransform="uppercase" letterSpacing="wider">
              Estado de cuenta
            </Text>
            {alDia && (
              <Badge colorScheme="green" borderRadius="full" px="8px" fontSize="xs" display="flex" alignItems="center" gap="4px">
                <Icon as={MdCheckCircle} w="10px" h="10px" /> Al Día
              </Badge>
            )}
            {hasDebt && (
              <Badge colorScheme="orange" variant="subtle" borderRadius="full" px="8px" fontSize="xs">
                Deuda pendiente
              </Badge>
            )}
          </HStack>
          {hasDebt && (
            <Button
              size="sm"
              colorScheme="brand"
              borderRadius="8px"
              leftIcon={<Icon as={MdCreditCard} />}
              onClick={() => setPayOpen(true)}
            >
              Registrar Pago
            </Button>
          )}
        </HStack>

        {/* Balances */}
        <Flex gap="0" flexWrap="wrap">
          <Flex flex="1" minW="120px" direction="column" gap="2px" pr="20px">
            <Text fontSize="xs" color={mutedColor}>Total Cargado</Text>
            <Text fontSize="xl" fontWeight="800" color={textColor}>{fmtMoney(totalCharged)}</Text>
          </Flex>

          <Divider orientation="vertical" h="48px" alignSelf="center" mx="4px" />

          <Flex flex="1" minW="120px" direction="column" gap="2px" px="20px">
            <Text fontSize="xs" color={mutedColor}>Total Pagado</Text>
            <Text fontSize="xl" fontWeight="800" color="green.500">{fmtMoney(totalPaid)}</Text>
          </Flex>

          <Divider orientation="vertical" h="48px" alignSelf="center" mx="4px" />

          <Flex flex="1" minW="120px" direction="column" gap="2px" pl="20px">
            <Text fontSize="xs" color={mutedColor}>Deuda Actual</Text>
            <Text fontSize="xl" fontWeight="800" color={hasDebt ? 'red.500' : 'green.500'}>
              {fmtMoney(currentDebt)}
            </Text>
          </Flex>
        </Flex>

      </Box>

      {/* Receipts history */}
      {(receipts.length > 0 || loadingReceipts) && (
        <Box
          bg={cardBg}
          border="1px solid"
          borderColor={borderColor}
          borderRadius="14px"
          overflow="hidden"
          mb="20px"
        >
          <Flex px="20px" py="14px" borderBottom="1px solid" borderColor={borderColor} align="center" justify="space-between">
            <Text fontSize="sm" fontWeight="800" color={textColor}>Recibos de pago</Text>
            <Badge colorScheme="green" borderRadius="full" px="10px" fontSize="11px">{receipts.length}</Badge>
          </Flex>

          {loadingReceipts && (
            <Box px="20px" py="12px">
              {[1,2].map(i => <Skeleton key={i} h="52px" borderRadius="8px" mb="8px" opacity={0.6} />)}
            </Box>
          )}

          {receipts.map(receipt => {
            const mType  = receipt.paymentMethod?.type as PaymentMethodType;
            const MethodIcon = METHOD_ICON[mType] ?? MdCreditCard;
            return (
              <Flex
                key={receipt.id}
                align="center" gap="14px"
                px="20px" py="12px"
                borderBottom="1px solid" borderColor={borderColor}
                _last={{ borderBottom: 'none' }}
              >
                <Flex w="36px" h="36px" borderRadius="9px" bg="green.50" align="center" justify="center" flexShrink={0}>
                  <Icon as={MethodIcon} w="17px" h="17px" color="green.500" />
                </Flex>
                <Box flex="1" minW={0}>
                  <HStack spacing="8px" flexWrap="wrap">
                    <Text fontSize="sm" fontWeight="700" color={textColor}>{fmtMoney(Number(receipt.amount))}</Text>
                    <Badge colorScheme="green" variant="subtle" borderRadius="full" px="8px" fontSize="10px">
                      {METHOD_LABEL[mType] ?? mType}
                    </Badge>
                    <Text fontSize="xs" color={mutedColor}>{receipt.receiptNumber}</Text>
                  </HStack>
                  <Text fontSize="xs" color={mutedColor} mt="1px">
                    {fmtDate(receipt.paidAt)}
                    {receipt.paymentMethod?.referenceNumber && ` · Ref: ${receipt.paymentMethod.referenceNumber}`}
                  </Text>
                </Box>
                <Icon as={MdCheckCircle} color="green.400" w="18px" h="18px" flexShrink={0} />
              </Flex>
            );
          })}
        </Box>
      )}

      {payOpen && (
        <RecordPaymentModal
          isOpen={payOpen}
          onClose={() => setPayOpen(false)}
          patientId={patientId}
          patientName={patientName}
          currentDebt={currentDebt}
        />
      )}
    </>
  );
}

// ─── Main tab ─────────────────────────────────────────────────────────────────

interface Props {
  patient: Patient;
}

export default function PagosTab({ patient }: Props) {
  const { data: account, isLoading: loadingAccount } = useBillingAccount(patient.id);

  const cardBg      = useColorModeValue('white',  'navy.800');
  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.100');
  const textColor   = useColorModeValue('gray.800', 'white');
  const mutedColor  = useColorModeValue('gray.500', 'gray.400');
  const emptyBg     = useColorModeValue('gray.50',  'navy.750');

  const charges = account?.charges ?? [];

  return (
    <Box p="20px">
      {/* Estado de cuenta + Recibos de pago */}
      <BillingAccountSummary
        patientId={patient.id}
        patientName={`${patient.nombres} ${patient.apellidos}`}
      />

      {/* Historial de cargos (BillingCharge) */}
      <Box bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="14px" overflow="hidden">
        <Flex px="20px" py="14px" borderBottom="1px solid" borderColor={borderColor} align="center" justify="space-between">
          <Text fontSize="sm" fontWeight="800" color={textColor}>Historial de cobros</Text>
          {charges.length > 0 && (
            <Badge colorScheme="blue" variant="subtle" borderRadius="full" px="10px" fontSize="11px">
              {charges.length} cargo{charges.length !== 1 ? 's' : ''}
            </Badge>
          )}
        </Flex>

        {loadingAccount && (
          <Box px="20px" py="12px">
            {[1,2,3].map(i => <Skeleton key={i} h="64px" borderRadius="8px" mb="8px" opacity={0.6} />)}
          </Box>
        )}

        {!loadingAccount && charges.length === 0 && (
          <Flex direction="column" align="center" py="40px" gap="10px">
            <Flex w="48px" h="48px" bg={emptyBg} borderRadius="14px" align="center" justify="center">
              <Icon as={MdAssignment} w="22px" h="22px" color={mutedColor} />
            </Flex>
            <Text fontSize="sm" fontWeight="600" color={textColor}>Sin cargos registrados</Text>
            <Text fontSize="xs" color={mutedColor}>Los cargos aparecen al registrar atenciones</Text>
          </Flex>
        )}

        {!loadingAccount && charges.map(charge => (
          <ChargeRow key={charge.id} charge={charge} />
        ))}
      </Box>
    </Box>
  );
}
